import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';
import { APP_CONFIG } from '../config/constants';

interface EmailNotConfirmedPageProps {
  showToast?: (message: string, type?: 'success' | 'error') => void;
}

export const EmailNotConfirmedPage: React.FC<EmailNotConfirmedPageProps> = ({ showToast }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setEmail(data.user?.email ?? null));
  }, []);

  const handleResendEmail = async () => {
    if (!email) {
      if (showToast) showToast('Email utilisateur non trouvé', 'error');
      return;
    }

    setSending(true);

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: { 
          emailRedirectTo: `${APP_CONFIG.url}/email-confirmed` 
        },
      });

      if (error) {
        if (showToast) showToast(`Erreur: ${error.message}`, 'error');
      } else {
        if (showToast) showToast(`Email de confirmation renvoyé à ${email} !`, 'success');
      }
    } catch (err) {
      if (showToast) showToast('Erreur lors de l\'envoi', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-orange-500 rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-envelope text-white text-2xl"></i>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Email non confirmé
          </h2>
          <p className="text-gray-600">
            Vous devez confirmer votre adresse email pour accéder à l'application
          </p>
        </div>

        {/* Contenu */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-800 mb-2">
                <i className="fas fa-exclamation-triangle text-orange-600 mr-2"></i>
                <strong>Votre email n'a pas encore été confirmé</strong>
              </p>
              {(user?.email || email) && (
                <p className="text-sm text-gray-700">
                  Email : <strong>{user?.email || email}</strong>
                </p>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm">
                Que faire ?
              </h3>
              <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
                <li>Vérifiez votre boîte mail (et le dossier spam)</li>
                <li>Cherchez un email de <strong>Tutormatch - HEG-Genève</strong></li>
                <li>Cliquez sur le lien de confirmation dans l'email</li>
                <li>Revenez vous connecter après confirmation</li>
              </ol>
            </div>

            <div className="space-y-3 pt-4">
              <button
                onClick={handleResendEmail}
                disabled={!email || sending}
                className="w-full py-3 px-4 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={!email ? "Email non trouvé" : ""}
              >
                <i className="fas fa-envelope mr-2"></i>
                {sending ? 'Envoi en cours...' : 'Renvoyer l\'email de confirmation'}
              </button>

              <button
                onClick={handleSignOut}
                className="w-full py-3 px-4 bg-[#002c46] text-white font-semibold rounded-lg hover:bg-[#001a2a] transition-colors"
              >
                <i className="fas fa-sign-out-alt mr-2"></i>
                Me déconnecter
              </button>

              <Link
                to="/contact"
                className="block w-full py-3 px-4 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors text-center"
              >
                <i className="fas fa-envelope mr-2"></i>
                Contacter le support
              </Link>
            </div>

            <div className="text-center pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                Vous n'avez pas reçu l'email ? Vérifiez vos spams ou contactez-nous.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

